import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext'; 
import { useProject } from '../context/ProjectContext'; 

export default function WorkOrders() {
  const { userInfo } = useAuth();
  const { selectedProjectID } = useProject();

  const [workOrders, setWorkOrders] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]); 
  const [loading, setLoading] = useState(true);
  
  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUrgency, setSelectedUrgency] = useState('ALL'); 

  // Galerija slika
  const [activePhotoGallery, setActivePhotoGallery] = useState(null);
  const [galleryTab, setGalleryTab] = useState('erp');
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState(null);

  // Novi nalog
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newOrder, setNewOrder] = useState({
    title: '',
    description: '',
    unitID: '',         
    unitText: '', 
    contractorID: '',
    priority: 'Medium',
    status: 'Pending',
    projectID: '', 
    images: [] 
  });

  // Uređivanje naloga
  const [selectedOrder, setSelectedOrder] = useState(null); 
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editUnitID, setEditUnitID] = useState('');        
  const [editUnitText, setEditUnitText] = useState(''); 
  const [editContractorID, setEditContractorID] = useState('');
  const [editPriority, setEditPriority] = useState('Medium');
  const [editStatus, setEditStatus] = useState('Pending');
  const [editProjectID, setEditProjectID] = useState(''); 
  const [editImages, setEditImages] = useState([]); 

  const companyID = userInfo?.companyID || 1;
  const COMMENT_SEPARATOR = "\n\n[Komentar kooperanta]:\n";

  // Listener za zatvaranje popupova i brisanje tražilice na tipku ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (fullscreenImageUrl) {
          setFullscreenImageUrl(null);
        } else if (activePhotoGallery) {
          setActivePhotoGallery(null);
        } else if (isCreateModalOpen) {
          setIsCreateModalOpen(false);
        } else if (selectedOrder) {
          setSelectedOrder(null);
        } else if (searchTerm) {
          setSearchTerm('');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCreateModalOpen, selectedOrder, activePhotoGallery, fullscreenImageUrl, searchTerm]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString('hr-HR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const saveActivity = async (type, body) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        companyID: String(companyID),
        userID: String(userInfo?.userID || '3'),
        type: type,
        body: body
      });

      await fetch(`${API_BASE_URL}/activityInsertRecord?${params.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Greška pri upisu aktivnosti:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const activeProjectID = selectedProjectID === 'Sve' ? '1' : selectedProjectID;
    const token = localStorage.getItem('token');
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    try {
      const [ordersRes, contractorsRes, unitsRes, projectsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/workOrderList?companyID=${companyID}`, { headers }),
        fetch(`${API_BASE_URL}/contractorList?companyID=${companyID}`, { headers }),
        fetch(`${API_BASE_URL}/unitList?projectID=${activeProjectID}`, { headers }),
        fetch(`${API_BASE_URL}/projectList?companyID=${companyID}`, { headers }) 
      ]);

      if (ordersRes.ok && contractorsRes.ok && unitsRes.ok && projectsRes.ok) {
        const ordersData = await ordersRes.json().catch(() => []);
        const contractorsData = await contractorsRes.json().catch(() => []);
        const unitsData = await unitsRes.json().catch(() => []);
        const projectsData = await projectsRes.json().catch(() => []);
        
        setWorkOrders(Array.isArray(ordersData) ? ordersData : []);
        setContractors(Array.isArray(contractorsData) ? contractorsData : []);
        setUnits(Array.isArray(unitsData) ? unitsData : []);
        setProjects(Array.isArray(projectsData) ? projectsData : []); 
      }
    } catch (error) {
      console.error("Greška pri dohvaćanju radnih naloga:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyID, selectedProjectID]);

  // PROŠIRENO FILTRIRANJE: Naslov, Opis, Stan (oznaka) i Izvođač (ime i struka)
  const filteredOrders = workOrders.filter(order => {
    const statusUpper = String(order.status || '').toUpperCase();
    const isCompleted = statusUpper === 'COMPLETED' || statusUpper === 'ZAVRŠENO' || statusUpper === 'ZAVRSENO' || statusUpper === 'GOTOVO';

    // 1. Pronađi izvođača
    const contractor = contractors.find(c => String(c.contractorID) === String(order.contractorID));
    const contractorText = contractor ? `${contractor.name} ${contractor.specialty || ''}` : '';

    // 2. Pronađi stan
    const unit = units.find(u => String(u.unitID) === String(order.unitID));
    const unitText = unit ? `Stan ${unit.unitNumber} ${unit.unitNumber}` : (order.unitText ? `Stan ${order.unitText} ${order.unitText}` : '');

    // 3. Spoji sve podatke u jedan pretraživi tekst
    const fullSearchableText = [
      order.title || '',
      order.description || '',
      contractorText,
      unitText
    ].join(' ').toLowerCase();

    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = term === '' || fullSearchableText.includes(term);

    if (!matchesSearch) return false;

    if (selectedUrgency === 'COMPLETED') return isCompleted;
    if (isCompleted) return false;
    if (selectedUrgency === 'ALL') return true;
    return order.priority?.toUpperCase() === selectedUrgency;
  });

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setNewOrder({
      title: '',
      description: '',
      unitID: '', 
      unitText: '',
      contractorID: contractors[0] ? String(contractors[0].contractorID) : '',
      priority: 'Medium',
      status: 'Pending',
      projectID: selectedProjectID && selectedProjectID !== 'Sve' ? String(selectedProjectID) : '', 
      images: [] 
    });
  };

  const handleSaveCreateOrder = () => {
    if (!newOrder.title.trim() || !newOrder.description.trim()) {
      alert("Molimo unesite naslov i opis naloga.");
      return;
    }

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('companyID', String(companyID));
    formData.append('unitID', newOrder.unitID || ''); 
    formData.append('contractorID', newOrder.contractorID);
    formData.append('title', newOrder.title);
    formData.append('description', newOrder.description);
    formData.append('priority', newOrder.priority);
    formData.append('status', newOrder.status);
    formData.append('unitText', newOrder.unitText || ''); 
    formData.append('projectID', newOrder.projectID || ''); 

    if (newOrder.images && newOrder.images.length > 0) {
      newOrder.images.forEach(file => {
        formData.append('image', file);
      });
    }

    fetch(`${API_BASE_URL}/workOrderInsertRecord`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })
    .then(res => {
      if (!res.ok) throw new Error("Neuspješno spremanje naloga.");
      return res.text();
    })
    .then(() => {
      const unitLabel = (units.find(u => String(u.unitID) === newOrder.unitID)?.unitNumber) || 'opcionalno';
      const userName = userInfo?.fullName || 'Korisnik';

      saveActivity(
        'work_order_insert', 
        `${userName} - kreiran radni nalog "${newOrder.title}" (${unitLabel})`
      );

      fetchData(); 
      setIsCreateModalOpen(false);
    })
    .catch(err => {
      console.error(err);
      alert("Greška pri spremanju radnog naloga.");
    });
  };

  const handleOpenEditModal = (order) => {
    setSelectedOrder(order);
    setEditTitle(order.title || '');
    setEditDescription(order.description || '');
    setEditUnitID(order.unitID ? String(order.unitID) : ''); 
    setEditUnitText(order.unitText || '');
    setEditContractorID(order.contractorID ? String(order.contractorID) : (contractors[0] ? String(contractors[0].contractorID) : ''));
    setEditPriority(order.priority || 'Medium');
    setEditStatus(order.status || 'Pending');

    const siteID = order.projectID !== undefined ? order.projectID : order.projectid;
    setEditProjectID(siteID ? String(siteID) : '');
    setEditImages([]); 
  };

  const handleSaveEditOrder = () => {
    if (!editTitle.trim() || !editDescription.trim()) {
      alert("Molimo unesite naslov i opis naloga.");
      return;
    }

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('orderID', selectedOrder.orderID);
    formData.append('companyID', String(companyID));
    formData.append('unitID', editUnitID || ''); 
    formData.append('contractorID', editContractorID);
    formData.append('title', editTitle);
    formData.append('description', editDescription);
    formData.append('priority', editPriority);
    formData.append('status', editStatus);
    formData.append('unitText', editUnitText || '');
    formData.append('projectID', editProjectID || '');

    if (editImages && editImages.length > 0) {
      editImages.forEach(file => {
        formData.append('image', file);
      });
    }

    fetch(`${API_BASE_URL}/workOrderUpdateRecord`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })
    .then(res => {
      if (res.ok) {
        const userName = userInfo?.fullName || 'Korisnik';

        saveActivity(
          'work_order_update', 
          `${userName} - ažuriran radni nalog "${editTitle}" (Status: ${editStatus})`
        );

        fetchData();
        setSelectedOrder(null);
      } else {
        alert("Greška pri spremanju promjena.");
      }
    })
    .catch(err => console.error(err));
  };

  const handleDeleteSingleImage = async (imageID, fileName) => {
    const confirmed = window.confirm("Želite li obrisati ovu sliku?");
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/workOrderDeleteImage?imageID=${imageID}&orderID=${selectedOrder.orderID}&fileName=${fileName}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchData(); 
        setSelectedOrder(prev => ({
          ...prev,
          images: prev.images.filter(img => img.imageID !== imageID)
        }));
      } else {
        alert("Greška pri brisanju slike.");
      }
    } catch (error) {
      console.error("Greška pri brisanju slike:", error);
    }
  };

  // Točkice i boje rubova
  const priorityDotColor = (priority) => {
    const p = String(priority || '').toUpperCase();
    if (p === 'HIGH' || p === 'EMERGENCY') return 'bg-red-500';
    if (p === 'MEDIUM') return 'bg-amber-500';
    return 'bg-blue-500';
  };

  const priorityColorClass = (priority) => {
    const p = String(priority || '').toUpperCase();
    if (p === 'HIGH' || p === 'EMERGENCY') return 'border-l-[3px] border-l-red-500';
    if (p === 'MEDIUM') return 'border-l-[3px] border-l-amber-500';
    return 'border-l-[3px] border-l-blue-500';
  };

  const statusStyleClass = (status) => {
    const s = String(status || '').toUpperCase();
    if (s === 'COMPLETED') return 'text-green-700 bg-green-50/70 border-green-200';
    if (s === 'IN PROGRESS') return 'text-amber-700 bg-amber-50/70 border-amber-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };

  const renderOrderCard = (order) => {
    const contractor = contractors.find(c => c.contractorID === order.contractorID);
    const unit = units.find(u => u.unitID === order.unitID);
    const isCompleted = String(order.status || '').toUpperCase() === 'COMPLETED' || String(order.status || '').toUpperCase() === 'ZAVRŠENO';

    const fullDescription = order.description || "";
    let displayDescription = fullDescription;
    let displayComment = "";

    if (fullDescription.includes(COMMENT_SEPARATOR)) {
      const parts = fullDescription.split(COMMENT_SEPARATOR);
      displayDescription = parts[0];
      displayComment = parts[1];
    }

    const unitLabel = unit
      ? `STAN: ${unit.unitNumber}`
      : (order.unitText ? `STAN: ${order.unitText}` : 'STAN: OPĆI');

    return (
      <div 
        key={order.orderID} 
        onClick={() => handleOpenEditModal(order)}
        className={`bg-white rounded-xl border border-slate-100/85 ${priorityColorClass(order.priority)} p-5 shadow-[0_2px_8px_rgba(0,0,0,0.03)] active:scale-[0.98] transition-all duration-100 cursor-pointer flex flex-col gap-3.5 text-left`}
      >
        <div className="space-y-2.5">
          <div className="flex justify-between items-center text-[11px] font-bold text-slate-400">
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${priorityDotColor(order.priority)}`} />
              <span className="text-indigo-600 font-extrabold uppercase">
                {unitLabel}
              </span>
            </div>
            <span className="uppercase text-[9px] tracking-wider text-slate-500 font-extrabold">
              {order.priority}
            </span>
          </div>

          <h3 className="text-base font-extrabold text-slate-800 leading-snug tracking-tight">
            {order.title}
          </h3>

          <p className="text-slate-500 text-xs leading-relaxed line-clamp-3 font-medium">
            {displayDescription}
          </p>

          {displayComment && (
            <div className="p-3 bg-indigo-50/50 border-l-2 border-indigo-500 rounded-r-xl text-xs">
              <p className="font-extrabold text-indigo-950 mb-0.5 text-[10px] uppercase tracking-wider">Update s terena (Kooperant):</p>
              <p className="whitespace-pre-line leading-relaxed font-semibold text-indigo-900 line-clamp-2">{displayComment}</p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-slate-400 font-semibold pt-1">
            <span>Zadao: <strong className="text-slate-600">{order.createdBy || "Korisnik"}</strong></span>
            <span>•</span>
            <span>{formatDate(order.createdAt)}</span>
            {isCompleted && order.completedAt && (
              <>
                <span>•</span>
                <span className="text-emerald-600 font-bold">Riješeno: {formatDate(order.completedAt)}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-slate-100 mt-1 text-xs">
          <div className="text-gray-500 font-medium truncate max-w-[50%]">
            Izvođač: <span className="font-semibold text-gray-700">{contractor ? contractor.name : 'Nije dodijeljen'}</span>
          </div>
          
          <div className="flex items-center gap-2">
            {order.images && order.images.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation(); 
                  setActivePhotoGallery(order.images); 
                  setGalleryTab('erp');
                }}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 text-indigo-700 active:scale-95 text-[10px] font-bold rounded-lg transition-all cursor-pointer shadow-sm border border-indigo-100/40"
              >
                <span className="material-symbols-outlined text-xs shrink-0 text-indigo-400">image</span>
                Slike ({order.images.length})
              </button>
            )}
            
            <span className={`px-2.5 py-1 rounded-full font-bold text-[9px] border tracking-wider uppercase ${statusStyleClass(order.status)}`}>
              {order.status}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl space-y-6 pb-12">
      
      {/* 1. ZAGLAVLJE */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Radni nalozi</h1>
        </div>
        
        <div className="flex items-center gap-2.5">
          <button 
            type="button"
            onClick={handleOpenCreateModal}
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs flex items-center gap-1.5 cursor-pointer"
          >
            <span className="text-base leading-none">+</span> Kreiraj nalog
          </button>

          <span className="text-xs font-bold bg-white border border-slate-200 px-3 py-2 rounded-xl text-slate-600 shadow-2xs">
            Ukupno: <strong className="text-slate-900">{filteredOrders.length}</strong>
          </span>
        </div>
      </div>

      {/* 2. PRETRAŽIVAČ + ORIGINALNI FILTERI */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row gap-3 items-center justify-between">
        
        {/* Pretraživač */}
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
            search
          </span>
          <input 
            type="text" 
            placeholder="Pretraži po nazivu, stanu ili izvođaču..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3.5 py-2 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-slate-400 transition" 
          />
        </div>

        {/* Filteri hitnosti */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {['ALL', 'HIGH', 'MEDIUM', 'LOW'].map((urgency) => (
            <button
              key={urgency}
              type="button"
              onClick={() => setSelectedUrgency(urgency)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer whitespace-nowrap shrink-0 ${
                selectedUrgency === urgency
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200/70'
              }`}
            >
              {urgency === 'ALL' ? 'Aktivni (Svi)' : urgency}
            </button>
          ))}

          <button
            type="button"
            onClick={() => setSelectedUrgency('COMPLETED')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1 ${
              selectedUrgency === 'COMPLETED'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200/80'
            }`}
          >
            <span className="material-symbols-outlined text-xs">check_circle</span>
            Završeni ({workOrders.filter(o => {
              const s = String(o.status || '').toUpperCase();
              return s === 'COMPLETED' || s === 'ZAVRŠENO' || s === 'ZAVRSENO' || s === 'GOTOVO';
            }).length})
          </button>
        </div>

      </div>

      {/* 3. MREŽA KARTICA NALOGA */}
      {loading ? (
        <p className="text-slate-400 text-center py-16 text-xs font-semibold">Učitavanje radnih naloga...</p>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white p-16 rounded-2xl border border-slate-200 shadow-xs text-center">
          <p className="text-xs text-slate-400 font-medium">Nema radnih naloga za odabranu hitnost ili pretragu.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {filteredOrders.map(order => renderOrderCard(order))}
        </div>
      )}

      {/* 4. MODAL ZA KREIRANJE NALOGA */}
      {isCreateModalOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/60 z-[90] flex items-center justify-center p-4 backdrop-blur-sm cursor-pointer"
          onClick={() => setIsCreateModalOpen(false)} 
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto cursor-default text-left"
            onClick={(e) => e.stopPropagation()} 
          >
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">Kreiraj novi nalog</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-slate-700 p-1 cursor-pointer font-bold text-sm">
                ✕
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Naziv zadatka (*)</label>
                <input type="text" placeholder="npr. Postavljanje pločica" value={newOrder.title} onChange={(e) => setNewOrder(prev => ({ ...prev, title: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Opis radova (*)</label>
                <textarea rows="2" placeholder="Upute za izvođača..." value={newOrder.description} onChange={(e) => setNewOrder(prev => ({ ...prev, description: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none resize-none" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Poveži sa stanom</label>
                  <select value={newOrder.unitID} onChange={(e) => setNewOrder(prev => ({ ...prev, unitID: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer">
                    <option value="">— Opći radovi —</option>
                    {units.map(u => (
                      <option key={u.unitID} value={String(u.unitID)}>
                        Stan {u.unitNumber}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Izvođač radova</label>
                  <select value={String(newOrder.contractorID)} onChange={(e) => setNewOrder(prev => ({ ...prev, contractorID: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer">
                    {contractors.map(c => <option key={c.contractorID} value={String(c.contractorID)}>{c.name} ({c.specialty})</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Hitnost</label>
                  <select value={newOrder.priority} onChange={(e) => setNewOrder(prev => ({ ...prev, priority: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer">
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Emergency">Emergency</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Status</label>
                  <select value={newOrder.status} onChange={(e) => setNewOrder(prev => ({ ...prev, status: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer">
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Priloži slike</label>
                <input 
                  type="file" 
                  accept="image/*"
                  multiple 
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewOrder(prev => ({ ...prev, images: files }));
                  }}
                  className="w-full text-xs text-slate-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" 
                />
              </div>
            </div>

            <div className="flex justify-end items-center pt-3 border-t border-slate-100 gap-2">
              <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold text-slate-700 cursor-pointer">Odustani</button>
              <button type="button" onClick={handleSaveCreateOrder} className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold cursor-pointer shadow-xs">Pošalji nalog</button>
            </div>
          </div>
        </div>
      )}

      {/* 5. MODAL ZA UREĐIVANJE NALOGA */}
      {selectedOrder && (
        <div 
          className="fixed inset-0 bg-slate-900/60 z-[90] flex items-center justify-center p-4 backdrop-blur-sm cursor-pointer"
          onClick={() => setSelectedOrder(null)} 
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto cursor-default text-left"
          >
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">Uredi nalog</h3>
              <button onClick={() => setSelectedOrder(null)} className="text-slate-400 hover:text-slate-700 p-1 cursor-pointer font-bold text-sm">
                ✕
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Naziv zadatka</label>
                <input 
                  type="text" 
                  value={editTitle} 
                  onChange={(e) => setEditTitle(e.target.value)} 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none" 
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Opis radova</label>
                <textarea 
                  rows="2" 
                  value={editDescription} 
                  onChange={(e) => setEditDescription(e.target.value)} 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none resize-none" 
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Stan</label>
                  <select value={editUnitID} onChange={(e) => setEditUnitID(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer">
                    <option value="">— Opći radovi —</option>
                    {units.map(u => (
                      <option key={u.unitID} value={String(u.unitID)}>
                        Stan {u.unitNumber}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Izvođač</label>
                  <select value={String(editContractorID)} onChange={(e) => setEditContractorID(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer">
                    {contractors.map(c => (
                      <option key={c.contractorID} value={String(c.contractorID)}>{c.name} ({c.specialty})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Hitnost</label>
                  <select value={editPriority} onChange={(e) => setEditPriority(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer">
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Emergency">Emergency</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Status radova</label>
                  <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer">
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>

              {/* Slike */}
              {selectedOrder.images && selectedOrder.images.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <label className="block text-[10px] font-bold uppercase text-slate-400">Postojeće slike</label>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedOrder.images.map((img) => (
                      <div key={img.imageID} className="relative rounded-xl overflow-hidden border border-slate-200 aspect-square bg-slate-50">
                        <img src={img.imageUrl} alt="Dokaz" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleDeleteSingleImage(img.imageID, img.fileName)}
                          className="absolute top-1 right-1 bg-black/60 hover:bg-rose-600 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center transition cursor-pointer"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Dodaj nove slike</label>
                <input 
                  type="file" 
                  accept="image/*"
                  multiple 
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setEditImages(files);
                  }}
                  className="w-full text-xs text-slate-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" 
                />
              </div>
            </div>

            <div className="flex justify-end items-center pt-3 border-t border-slate-100 gap-2">
              <button type="button" onClick={() => setSelectedOrder(null)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold text-slate-700 cursor-pointer">Odustani</button>
              <button type="button" onClick={handleSaveEditOrder} className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold cursor-pointer shadow-xs">Spremi izmjene</button>
            </div>
          </div>
        </div>
      )}

      {/* 6. GALERIJA SLIKA (LIGHTBOX) */}
      {activePhotoGallery && (
        <div 
          className="fixed inset-0 bg-slate-900/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm cursor-pointer"
          onClick={() => setActivePhotoGallery(null)} 
        >
          <div 
            className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[85vh] overflow-y-auto cursor-default"
            onClick={(e) => e.stopPropagation()} 
          >
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">
                Slike radnog naloga ({activePhotoGallery.length})
              </h3>
              <button onClick={() => setActivePhotoGallery(null)} className="text-slate-400 hover:text-slate-700 p-1 cursor-pointer font-bold text-sm">
                ✕
              </button>
            </div>

            {/* Tabovi */}
            <div className="flex border-b border-slate-100 gap-3">
              <button
                type="button"
                onClick={() => setGalleryTab('erp')}
                className={`pb-2.5 text-xs font-bold transition cursor-pointer ${
                  galleryTab === 'erp' 
                    ? 'text-slate-900 border-b-2 border-slate-900' 
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Slike iz ureda ({activePhotoGallery.filter(img => (img.uploadedBy || 'erp') === 'erp').length})
              </button>
              <button
                type="button"
                onClick={() => setGalleryTab('contractor')}
                className={`pb-2.5 text-xs font-bold transition cursor-pointer ${
                  galleryTab === 'contractor' 
                    ? 'text-slate-900 border-b-2 border-slate-900' 
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Slike s gradilišta ({activePhotoGallery.filter(img => img.uploadedBy === 'contractor').length})
              </button>
            </div>
            
            {/* Slike mreža */}
            {activePhotoGallery.filter(img => (img.uploadedBy || 'erp') === galleryTab).length === 0 ? (
              <p className="text-center py-8 text-xs text-slate-400 italic">Nema slika u ovoj kategoriji.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 pt-1">
                {activePhotoGallery
                  .filter(img => (img.uploadedBy || 'erp') === galleryTab)
                  .map((img, index) => (
                    <div 
                      key={img.imageID || index} 
                      className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-video relative group cursor-zoom-in"
                      onClick={() => setFullscreenImageUrl(img.imageUrl.trim())}
                    >
                      <img 
                        src={img.imageUrl.trim()} 
                        alt="Slika" 
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-200" 
                      />
                    </div>
                  ))}
              </div>
            )}

            <div className="pt-2 border-t border-slate-100">
              <button 
                onClick={() => setActivePhotoGallery(null)}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
              >
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. FULLSCREEN SLIKA */}
      {fullscreenImageUrl && (
        <div 
          className="fixed inset-0 bg-black/90 z-[110] flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setFullscreenImageUrl(null)}
        >
          <button 
            onClick={() => setFullscreenImageUrl(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 cursor-pointer font-bold"
          >
            ✕
          </button>
          <img 
            src={fullscreenImageUrl} 
            alt="Pregled" 
            className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
          />
        </div>
      )}

    </div>
  );
}